// src/stores/useAppStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Language } from '../locales/translations';
import { ColorGradientType, ThemeType } from '../styles/themeGradients';
import { Classroom, Teacher } from '../types';

interface AppState {
  currentTeacher: Teacher | null;
  currentClassroom: Classroom | null;
  isLoading: boolean;
  theme: ThemeType;
  language: Language;
  accentColor: ColorGradientType | 'none';
  setCurrentTeacher: (teacher: Teacher | null) => void;
  setCurrentClassroom: (classroom: Classroom | null) => void;
  setLoading: (loading: boolean) => void;
  setTheme: (theme: ThemeType) => void;
  setLanguage: (language: Language) => void;
  setAccentColor: (color: ColorGradientType | 'none') => void;
}

/**
 * Global Zustand store for managing application-wide state with persistence.
 * Handles teacher authentication, current classroom context, loading states, and user preferences
 * (theme, language, accent color). Uses Zustand middleware for automatic localStorage persistence
 * of theme, language, and accent color preferences across sessions.
 *
 * @returns Store hook with state and actions for managing app-level state
 *
 * @example
 * ```tsx
 * function App() {
 *   const { currentTeacher, theme, setTheme } = useAppStore();
 *
 *   return (
 *     <div>
 *       {currentTeacher && <p>Welcome, {currentTeacher.firstName}</p>}
 *       <button onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
 *         Toggle Theme
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * function ClassroomView() {
 *   const { currentClassroom, setCurrentClassroom, setLoading } = useAppStore();
 *
 *   useEffect(() => {
 *     setLoading(true);
 *     fetchClassroom().then(classroom => {
 *       setCurrentClassroom(classroom);
 *       setLoading(false);
 *     });
 *   }, []);
 *
 *   return <div>{currentClassroom?.name}</div>;
 * }
 * ```
 */
export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      currentTeacher: null,
      currentClassroom: null,
      isLoading: true, // Start as loading to prevent redirect on initial render
      theme: 'light', // Default to light theme
      language: 'en', // Default language
      accentColor: 'none', // Default no accent color
      setCurrentTeacher: (teacher) => set({ currentTeacher: teacher }),
      setCurrentClassroom: (classroom) => set({ currentClassroom: classroom }),
      setLoading: (loading) => set({ isLoading: loading }),
      setTheme: (theme) => {
        set({ theme });
        // Apply theme class to document root for CSS variable inheritance
        document.documentElement.classList.remove('theme-light', 'theme-dark', 'theme-neon');
        document.documentElement.classList.add(`theme-${theme}`);

        // Apply matching body background
        const bodyBg: Record<ThemeType, string> = {
          dark: 'bg-[#060A16]',
          light: 'bg-[#F8FAFC]',
          neon: 'bg-[#050816]',
        };
        document.body.className = `${bodyBg[theme]} transition-colors duration-300`;
      },
      setLanguage: (language) => set({ language }),
      setAccentColor: (accentColor) => set({ accentColor }),
    }),
    {
      name: 'app-storage',
      partialize: (state) => ({
        theme: state.theme,
        language: state.language,
        accentColor: state.accentColor,
      }),
    }
  )
);
